import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { getPerson, getPersonGuestPreview } from '@/api/query/person';
import Skeleton from '@/common/components/skeleton/Skeleton';
import useImageLoader, { normalizeFileId } from '@/common/hooks/useImage';
import useVideoLoader from '@/common/hooks/useVideo';
import useVideoReady from '@/common/hooks/useVideoReady';
import { playVideoSafely } from '@/common/functions/videoElement';
import { useLanguage, useLanguageData } from '@/common/hooks/useLanguageData';
import { isAuthenticated, useGuestAuth } from '@/common/contexts/GuestAuthContext';
import { REGISTRATION_SOURCE } from '@/common/functions/getUtms';
import { formatTranslation } from '@/common/utils/formatTranslation';
import {
	getProfileAttributes,
	getProfileGallerySlides,
	getProfileMeta,
} from './getProfileAttributes';
import { PROFILE_ATTRIBUTE_ICONS } from './profileAttributeIcons';
import {
	GallerySkeleton,
	GalleryVideo,
	ProfileModalImage,
	ProfileAttributeCopy,
	ProfileAttributeGrid,
	ProfileAttributeIcon,
	ProfileAttributeItem,
	ProfileAttributeSection,
	ProfileAttributes,
	ProfileModal,
	ProfileModalBio,
	ProfileModalCard,
	ProfileModalClose,
	ProfileModalChat,
	ProfileModalDetails,
	ProfileModalDot,
	ProfileModalGallery,
	ProfileModalMeta,
	ProfileModalName,
	ProfileModalNext,
	ProfileModalPrev,
	ProfileModalThumbs,
} from './profileDetailsModal.styles';

const ProfileDetailsModal = ({ profile, onClose, hideStartChat = false }) => {
	const navigate = useNavigate();
	const { openAuthModal } = useGuestAuth();
	const { lang } = useLanguage();
	const t = useLanguageData('profileModal');
	const [resolvedProfile, setResolvedProfile] = useState(profile);

	const loadProfileMutation = useMutation({
		mutationFn: ({ personId, language }) => (
			isAuthenticated()
				? getPerson(personId, { language })
				: getPersonGuestPreview(personId, { language })
		),
		onSuccess: ({ data }) => {
			if (!data) {
				return;
			}

			setResolvedProfile((prev) => ({ ...prev, ...data }));
		},
	});

	useEffect(() => {
		setResolvedProfile(profile);

		if (!profile?._id) {
			return;
		}

		loadProfileMutation.mutate({ personId: profile._id, language: lang });
	}, [profile?._id, lang]);

	const videoMediaId = normalizeFileId(
		resolvedProfile?.profile_video?._id
		?? resolvedProfile?.profile_video
		?? resolvedProfile?.video?._id
		?? resolvedProfile?.video,
	);
	const { videoSrc } = useVideoLoader(videoMediaId);
	const videoRef = useRef(null);
	const imageRef = useRef(null);
	const [galleryIndex, setGalleryIndex] = useState(0);
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const videoRemountKey = `${videoMediaId ?? ''}-${videoSrc ?? ''}`;
	const isVideoReady = useVideoReady(videoRef, videoSrc, videoRemountKey);
	const hasVideo = Boolean(videoSrc);

	const gallerySlides = useMemo(
		() => getProfileGallerySlides(resolvedProfile),
		[resolvedProfile],
	);

	const currentSlide = gallerySlides[galleryIndex];
	const isVideoSlide = currentSlide?.type === 'video';
	const currentImageFileId = currentSlide?.type === 'image' ? currentSlide.fileId : null;
	const profileImageFileId = normalizeFileId(resolvedProfile?.profile_image);
	const { imageSrc: profileImageSrc } = useImageLoader(profileImageFileId);
	const { imageSrc, isLoading } = useImageLoader(currentImageFileId);

	const attributeSections = useMemo(
		() => getProfileAttributes(resolvedProfile, t, lang),
		[resolvedProfile, t, lang],
	);

	const profileMeta = useMemo(
		() => getProfileMeta(resolvedProfile, t),
		[resolvedProfile, t],
	);

	const bio =
		resolvedProfile?.description
		|| resolvedProfile?.person_config?.description
		|| '';

	const isFileReady = !currentImageFileId || !isLoading;
	const showImageSkeleton =
		!isVideoSlide && (!isFileReady || !imageSrc || !isImageLoaded);
	const showVideoSkeleton = isVideoSlide && hasVideo && !isVideoReady;

	useEffect(() => {
		setGalleryIndex(0);
	}, [resolvedProfile?._id]);

	useEffect(() => {
		if (galleryIndex >= gallerySlides.length && gallerySlides.length > 0) {
			setGalleryIndex(0);
		}
	}, [galleryIndex, gallerySlides.length]);

	useEffect(() => {
		setIsImageLoaded(false);
	}, [imageSrc]);

	useEffect(() => {
		const element = imageRef.current;

		if (!element || !imageSrc) {
			return;
		}

		if (element.complete && element.naturalWidth > 0) {
			setIsImageLoaded(true);
		}
	}, [imageSrc]);

	useEffect(() => {
		if (!isVideoSlide || !videoRef.current) {
			if (videoRef.current) {
				videoRef.current.pause();
				videoRef.current.currentTime = 0;
			}

			return;
		}

		playVideoSafely(videoRef.current);
	}, [galleryIndex, isVideoSlide, videoSrc]);

	const handleActionClick = () => {
		if (!isAuthenticated()) {
			onClose();
			openAuthModal(
				'register',
				`/chat/${resolvedProfile._id}`,
				REGISTRATION_SOURCE.CHAT,
			);
			return;
		}
		navigate(`/chat/${resolvedProfile._id}`);
		onClose();
	};

	const handleImageLoad = () => {
		setIsImageLoaded(true);
	};

	const handleImageError = () => {
		setIsImageLoaded(true);
	};

	const goToPrev = () => {
		if (gallerySlides.length <= 1) {
			return;
		}

		setGalleryIndex(
			(index) => (index - 1 + gallerySlides.length) % gallerySlides.length,
		);
	};

	const goToNext = () => {
		if (gallerySlides.length <= 1) {
			return;
		}

		setGalleryIndex((index) => (index + 1) % gallerySlides.length);
	};

	useEffect(() => {
		const { body, documentElement } = document;
		const previousBodyOverflow = body.style.overflow;
		const previousHtmlOverflow = documentElement.style.overflow;

		body.style.overflow = 'hidden';
		documentElement.style.overflow = 'hidden';

		return () => {
			body.style.overflow = previousBodyOverflow;
			documentElement.style.overflow = previousHtmlOverflow;
		};
	}, []);

	const modalContent = (
		<ProfileModal aria-hidden="false" onClick={onClose}>
			<ProfileModalCard
				role="dialog"
				aria-modal="true"
				aria-labelledby="profileModalName"
				onClick={(e) => e.stopPropagation()}
			>
				<ProfileModalClose type="button" aria-label={t.close} onClick={onClose}>
					×
				</ProfileModalClose>
				<ProfileModalGallery aria-label={t.characterGallery}>
					<ProfileModalPrev
						type="button"
						aria-label={t.previousPhoto}
						onClick={goToPrev}
						disabled={gallerySlides.length <= 1}
					>
						‹
					</ProfileModalPrev>
					{(showImageSkeleton || showVideoSkeleton) && (
						<GallerySkeleton>
							<Skeleton
								width="100%"
								height="100%"
								minWidth="100%"
								minHeight="100%"
								radius="0"
							/>
						</GallerySkeleton>
					)}
					{!isVideoSlide && imageSrc && (
						<ProfileModalImage
							ref={imageRef}
							id="profileModalImage"
							src={imageSrc}
							alt={resolvedProfile.name}
							$visible={isImageLoaded}
							onLoad={handleImageLoad}
							onError={handleImageError}
						/>
					)}
					{hasVideo && (
						<GalleryVideo
							ref={videoRef}
							key={videoRemountKey}
							src={videoSrc}
							muted
							loop
							playsInline
							preload="auto"
							draggable={false}
							$visible={isVideoSlide && isVideoReady}
							poster={profileImageSrc || undefined}
						/>
					)}
					<ProfileModalNext
						type="button"
						aria-label={t.nextPhoto}
						onClick={goToNext}
						disabled={gallerySlides.length <= 1}
					>
						›
					</ProfileModalNext>
					{gallerySlides.length > 1 && (
						<ProfileModalThumbs id="profileModalThumbs">
							{gallerySlides.map((slide, index) => (
								<ProfileModalDot
									key={`${slide.type}-${slide.fileId}`}
									type="button"
									$active={index === galleryIndex}
									aria-label={formatTranslation(t.photoNumber, {
										number: index + 1,
									})}
									onClick={() => setGalleryIndex(index)}
								/>
							))}
						</ProfileModalThumbs>
					)}
					{!hideStartChat && (
						<ProfileModalChat type="button" onClick={handleActionClick}>
							{t.startChatting}
						</ProfileModalChat>
					)}
				</ProfileModalGallery>
				<ProfileModalDetails>
					<ProfileModalMeta>{profileMeta}</ProfileModalMeta>
					<ProfileModalName id="profileModalName">{resolvedProfile.name}</ProfileModalName>
					{bio && <ProfileModalBio id="profileModalBio">{bio}</ProfileModalBio>}
					<ProfileAttributes id="profileModalAttributes">
						{attributeSections.map((section) => (
							<ProfileAttributeSection
								key={section.title}
								aria-label={section.title}
							>
								<h3>{section.title}</h3>
								<ProfileAttributeGrid>
									{section.items.map((item) => {
										const Icon = PROFILE_ATTRIBUTE_ICONS[item.icon];

										return (
											<ProfileAttributeItem key={item.label}>
												<ProfileAttributeIcon>
													{Icon ? <Icon /> : null}
												</ProfileAttributeIcon>
												<ProfileAttributeCopy>
													<strong>{item.label}</strong>
													<span>{item.value}</span>
												</ProfileAttributeCopy>
											</ProfileAttributeItem>
										);
									})}
								</ProfileAttributeGrid>
							</ProfileAttributeSection>
						))}
					</ProfileAttributes>
				</ProfileModalDetails>
			</ProfileModalCard>
		</ProfileModal>
	);

	return createPortal(modalContent, document.body);
};

export default ProfileDetailsModal;
